import { useEffect, useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import login_illustration from "../../Assets/Login/login_illustration.png";
import { useAdminAuth } from "../../context/AdminAuthContext";

const AdminLogin = () => {
  const navigate = useNavigate();
  const { loginAdmin, forgotPassword, btnLoading } = useAdminAuth();

  const [mode, setMode] = useState("login"); // login | reset
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    email: "",
    password: "",
    phone: "",
    newPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);

  const [errors, setErrors] = useState({
    email: "",
    password: "",
    phone: "",
    newPassword: "",
  });

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateLogin = () => {
    const e = { email: "", password: "" };
    let valid = true;

    if (!form.email) {
      e.email = "Email is required";
      valid = false;
    }

    if (!form.password) {
      e.password = "Password is required";
      valid = false;
    }

    setErrors((prev) => ({ ...prev, ...e }));
    return valid;
  };

  const validateReset = () => {
    const e = { email: "", phone: "", newPassword: "" };
    let valid = true;

    if (!form.email) {
      e.email = "Email is required";
      valid = false;
    }

    if (!form.phone) {
      e.phone = "Phone number is required";
      valid = false;
    }

    if (!form.newPassword) {
      e.newPassword = "New password required";
      valid = false;
    }

    setErrors((prev) => ({ ...prev, ...e }));
    return valid;
  };

  const handleLogin = async () => {
    if (!validateLogin()) return;

    const res = await loginAdmin({
      email: form.email,
      password: form.password,
    });

    if (res.success) navigate("/admin/dashboard");
  };

  const handleReset = async () => {
    if (!validateReset()) return;

    const res = await forgotPassword({
      email: form.email,
      phone: form.phone,
      newPassword: form.newPassword,
    });

    if (res.success) setMode("login");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (mode === "login") {
      handleLogin();
    } else {
      handleReset();
    }
  };

  useEffect(() => {
    setErrors({
      email: "",
      password: "",
      phone: "",
      newPassword: "",
    });

    setSubmitted(false);

    setForm((prev) => ({
      ...prev,
      password: "",
      phone: "",
      newPassword: "",
    }));

    document.getElementById("emailInput")?.focus();
  }, [mode]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-surfaceAccentLight px-4">
      <div className="w-full max-w-6xl bg-white shadow-2xl grid grid-cols-1 lg:grid-cols-2 ">
        {/* IMAGE SIDE */}
        <div className="hidden lg:block">
          <img
            src={login_illustration}
            className="h-full w-full object-cover object-right"
            alt="login_illustration"
          />
        </div>

        {/* FORM SIDE */}
        <div className="p-10 flex flex-col justify-center">
          <div className="flex gap-6 items-center mb-10">
            <img src="/logo.png" className="h-[4.5rem] w-fit" alt="logo" />

            <div>
              <h2 className="text-2xl font-semibold font-heading text-headingText mb-1">
                {mode === "login" ? "Welcome Back" : "Reset Password"}
              </h2>

              <p className="text-sm text-descriptionText">
                {mode === "login"
                  ? "Login to access admin dashboard"
                  : "Enter your details to reset password"}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* EMAIL */}
            <div>
              <label className="text-sm font-medium text-headingText">
                Email Address
              </label>

              <input
                id="emailInput"
                type="email"
                placeholder="Enter your email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                className={`mt-1 h-[44px] w-full px-4 border outline-none
                ${submitted && errors.email ? "border-danger" : "border-borderLight"}
                focus:border-primaryAccent`}
              />

              {submitted && errors.email && (
                <div className="text-xs text-danger mt-1">{errors.email}</div>
              )}
            </div>

            {/* LOGIN PASSWORD */}
            {mode === "login" && (
              <div>
                <label className="text-sm font-medium text-headingText">
                  Password
                </label>

                <div className="relative mt-1">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={form.password}
                    onChange={(e) => updateField("password", e.target.value)}
                    className={`h-[44px] w-full px-4 pr-10 border outline-none
                    ${submitted && errors.password ? "border-danger" : "border-borderLight"}
                    focus:border-primaryAccent`}
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-descriptionText"
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>

                <div className="h-[16px] text-xs text-danger mt-1">
                  {submitted && errors.password}
                </div>
              </div>
            )}

            {/* RESET PHONE */}
            {mode === "reset" && (
              <div>
                <label className="text-sm font-medium text-headingText">
                  Phone Number
                </label>

                <input
                  placeholder="Enter registered phone"
                  value={form.phone}
                  maxLength={10}
                  onChange={(e) => updateField("phone", e.target.value)}
                  className={`mt-1 h-[44px] w-full px-4 border outline-none
                  ${submitted && errors.phone ? "border-danger" : "border-borderLight"}
                  focus:border-primaryAccent`}
                />
                {submitted && (
                  <div className="h-[16px] text-xs text-danger mt-1">
                    {errors.phone}
                  </div>
                )}
              </div>
            )}

            {/* RESET PASSWORD */}
            {mode === "reset" && (
              <div>
                <label className="text-sm font-medium text-headingText">
                  New Password
                </label>

                <input
                  type="password"
                  placeholder="Enter new password"
                  value={form.newPassword}
                  onChange={(e) => updateField("newPassword", e.target.value)}
                  className={`mt-1 h-[44px] w-full px-4 border outline-none
                  ${submitted && errors.newPassword ? "border-danger" : "border-borderLight"}
                  focus:border-primaryAccent`}
                />

                <div className="h-[16px] text-xs text-danger mt-1">
                  {submitted && errors.newPassword}
                </div>
              </div>
            )}

            {/* BUTTON */}
            <div className="h-[52px] flex flex-col justify-end">
              <button
                type="submit"
                disabled={btnLoading}
                className="h-[44px] w-full self-end bg-btnColor hover:bg-btnHoverColor text-white font-medium flex items-center justify-center gap-2"
              >
                {btnLoading && (
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                )}

                {mode === "login"
                  ? btnLoading
                    ? "Logging in..."
                    : "Log In"
                  : btnLoading
                    ? "Resetting..."
                    : "Reset Password"}
              </button>
            </div>

            {/* TOGGLE */}
            <div className="text-right text-sm text-primaryAccent">
              {mode === "login" ? (
                <button type="button" onClick={() => setMode("reset")}>
                  Forgot your password?
                </button>
              ) : (
                <button type="button" onClick={() => setMode("login")}>
                  Back to login
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
